import { Plus, Bot, SearchX, FolderOpen } from "lucide-react";
import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "../../components/ui/dropdown";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { cn } from "../../lib/utils";
import { AiLauncher } from "./ai-launcher";
import { AgentCard } from "./agent-card";
import { CreateAgentModal } from "./create-agent-modal";
import { EmptyState } from "./empty-state";
import { FolderSidebar } from "./folder-sidebar";
import { KpiCards } from "./kpi-cards";
import { PinnedWidgets } from "./pinned-widgets";
import { TestCallModal } from "./test-call-modal";
import { useAgents, useAgentsAnalytics, useCreditAlerts, useWorkflows } from "./queries";
import type { Agent, AgentAnalytics, Workflow } from "./types";
import { useAuth } from "../../context/AuthContext";
import { describeAccessDenial } from "../../lib/access-denial";
import { canManageAgents } from "../../lib/userAccess";
import { WorkflowCard } from "./workflow-card";
import { getWorkflowId } from "./card-routes";
import {
  AGENT_CARD_SORT_OPTIONS,
  sortAgentCards,
  type AgentCardSort,
  type SortableAgentCard,
} from "./agent-card-sort";

type DatePreset = "7d" | "30d" | "all";

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All time" },
];

function getDateParams(preset: DatePreset): { start_date?: string; end_date?: string } {
  if (preset === "all") return {};
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (preset === "7d" ? 7 : 30));
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start_date: fmt(start), end_date: fmt(end) };
}

type DisplayCard = SortableAgentCard & (
  | { kind: "agent"; key: string; agent: Agent; stats?: AgentAnalytics }
  | { kind: "workflow"; key: string; workflow: Workflow; stats?: AgentAnalytics }
);

export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  useCreditAlerts();
  const [datePreset, setDatePreset] = usePersistentState<DatePreset>("home:date-preset", "7d");
  const dateParams = useMemo(() => getDateParams(datePreset), [datePreset]);
  const agents = useAgents();
  const workflows = useWorkflows();
  const analytics = useAgentsAnalytics(dateParams);
  const [folder, setFolder] = useState<string | null>(null);
  const [testAgent, setTestAgent] = useState<Agent | null>(null);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = usePersistentState<AgentCardSort>("home:agent-card-sort", "modified_desc");
  const [collapsed] = usePersistentState("sidebar:collapsed", false);
  const { user } = useAuth();
  const searchQuery = (searchParams.get("q") || "").trim().toLowerCase();

  const visibleAgents = (agents.data ?? []).filter((agent) => {
    if (folder && agent.folder_id !== folder) return false;
    if (!searchQuery) return true;
    return [agent.agent_name, agent.name, agent.description, agent.agent_id]
      .some((value) => String(value || "").toLowerCase().includes(searchQuery));
  });
  const visibleWorkflows = (workflows.data ?? []).filter((workflow) => {
    if (folder && workflow.folder_id !== folder) return false;
    if (!searchQuery) return true;
    return [workflow.name, workflow.agent_name, workflow.description, getWorkflowId(workflow)]
      .some((value) => String(value || "").toLowerCase().includes(searchQuery));
  });
  const agentStatFor = (agent: Agent) => analytics.data?.find((item) => (
    agent.multi_agent_config_id
      ? item.multi_agent_config_id === agent.multi_agent_config_id
      : item.agent_type !== "multi" && item.agent_id === agent.agent_id
  ));
  const workflowStatFor = (id: string) => analytics.data?.find(
    (item) => item.multi_agent_config_id === id,
  );
  const cards: DisplayCard[] = [
    ...visibleWorkflows.map((workflow): DisplayCard => {
      const stats = workflowStatFor(getWorkflowId(workflow));
      // Must match what WorkflowCard renders, or sorting by calls disagrees
      // with the number on the card.
      const calls = workflow.total_calls ?? stats?.total_calls ?? 0;
      return {
        kind: "workflow",
        key: `workflow:${getWorkflowId(workflow)}`,
        workflow,
        stats,
        name: workflow.name || workflow.agent_name || "Untitled Workflow",
        modifiedAt: workflow.updated_at || workflow.created_at,
        lastActivityAt: stats?.last_call_at || workflow.last_active || workflow.updated_at,
        calls,
        successRate: calls > 0 && stats ? Math.round(((stats.connected_calls || 0) / calls) * 100) : 0,
      };
    }),
    ...visibleAgents.map((agent): DisplayCard => {
      const stats = agentStatFor(agent);
      // Must match what AgentCard renders (lifetime, not the analytics window).
      const calls = agent.total_calls ?? stats?.total_calls ?? 0;
      return {
        kind: "agent",
        key: `agent:${agent.agent_id}`,
        agent,
        stats,
        name: agent.agent_name || agent.name || "Untitled Agent",
        modifiedAt: agent.updated_at || agent.created_at,
        lastActivityAt: stats?.last_call_at || agent.last_active || agent.updated_at,
        calls,
        successRate: calls > 0 && stats ? Math.round(((stats.connected_calls || 0) / calls) * 100) : 0,
      };
    }),
  ];
  const sortedCards = sortAgentCards(cards, sort);

  const failed = agents.isError || workflows.isError || analytics.isError;
  // A 403 is the owner's decision, not a fault: say what is missing instead of "couldn't load".
  const denial = failed ? describeAccessDenial([agents.error, workflows.error, analytics.error]) : null;

  return (
    <div className="flex flex-col">
      {failed && denial && (
        <div role="status" className="mx-8 mt-6 rounded-lg border border-line bg-soft px-4 py-3 text-sm text-ink-2">
          <p className="font-semibold text-ink">{denial.title}</p>
          <p className="mt-0.5">{denial.detail}</p>
        </div>
      )}
      {failed && !denial && (
        <div className="mx-8 mt-6 flex items-center justify-between rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
          Couldn't load dashboard data.
          <button onClick={() => { agents.refetch(); workflows.refetch(); analytics.refetch(); }} className="underline">Retry</button>
        </div>
      )}

      <div className="space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">

      {/* Date filter + KPI cards */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 max-w-full">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDatePreset(p.value)}
              className={cn(
                "rounded-full px-3 py-0.5 text-xs font-medium transition-colors",
                datePreset === p.value
                  ? "bg-accent-500 text-ink shadow-sm font-semibold hover:bg-accent-600"
                  : "bg-soft text-ink-muted hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-500/10 dark:hover:text-accent-400",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <KpiCards dateParams={dateParams} />
      </div>

      {(user?.command_center !== false || canManageAgents(user)) && <AiLauncher />}
      <PinnedWidgets />

      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <FolderSidebar active={folder} onSelect={setFolder} />
        <div className="min-w-0 flex-1">
          {/* ── Toolbar: sort + create ──────────────────────────────── */}
          <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-mono text-[10px] font-bold uppercase tracking-wider text-ink-faint">
                Agents <span className="text-ink-muted">· {sortedCards.length}</span>
              </h2>
              <div className="w-44">
                <Dropdown
                  ariaLabel="Sort agent cards"
                  value={sort}
                  options={AGENT_CARD_SORT_OPTIONS}
                  onChange={setSort}
                  className="h-7 bg-elevated py-0.5 text-xs"
                />
              </div>
            </div>
            {canManageAgents(user) && (
              <button
                onClick={() => setCreating(true)}
                className="flex items-center gap-1 rounded-lg bg-accent-500 px-3 py-1.5 text-xs font-semibold text-ink transition-colors duration-normal hover:bg-accent-600 focus-visible:ring-2 focus-visible:ring-ink dark:focus-visible:ring-[#FAFAF9]"
              >
                <Plus className="h-3.5 w-3.5" /> Create Agent
              </button>
            )}
          </div>

          {/* ── Empty states ────────────────────────────────────────── */}
          {!agents.isLoading && !workflows.isLoading && sortedCards.length === 0 ? (
            searchQuery ? (
              /* Search reduced to zero */
              <EmptyState
                icon={SearchX}
                title={`No results for “${searchQuery}”`}
                description="Check your spelling or try a different name."
                action={{
                  label: "Clear search",
                  onClick: () => setSearchParams((p) => { p.delete("q"); return p; }),
                }}
              />
            ) : folder ? (
              /* Folder is empty */
              <EmptyState
                icon={FolderOpen}
                title="No agents in this folder"
                description="Drag agents here from All Agents or create a new one."
                action={{ label: "Show all agents", onClick: () => setFolder(null) }}
              />
            ) : (
              /* Truly no agents yet */
              <EmptyState
                icon={Bot}
                title="No agents yet"
                description="Create your first voice agent to get started."
                action={canManageAgents(user) ? { label: "Create agent", onClick: () => setCreating(true) } : undefined}
              />
            )
          ) : (
            /* ── Card grid ─────────────────────────────────────────── */
            <div
              className={cn(
                "grid grid-cols-1 gap-4 md:grid-cols-2",
                collapsed && "lg:grid-cols-3",
              )}
            >
              {agents.isLoading || workflows.isLoading
                ? [0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-56 animate-pulse rounded-xl bg-soft" />
                  ))
                : sortedCards.map((card) =>
                    card.kind === "workflow" ? (
                      <WorkflowCard
                        key={card.key}
                        workflow={card.workflow}
                        stats={card.stats}
                        canEdit={canManageAgents(user)}
                        onTestCall={(w) => {
                          setTestAgent({
                            agent_id: `workflow-${w.config_id || w.id || "unpublished"}`,
                            agent_name: w.name || w.agent_name || "Untitled Workflow",
                            description: w.description,
                            folder_id: w.folder_id ?? null,
                            agent_type: "multi",
                            multi_agent_config_id: w.config_id || w.id || null,
                          });
                        }}
                      />
                    ) : (
                      <AgentCard
                        key={card.key}
                        agent={card.agent}
                        stats={card.stats}
                        onTestCall={setTestAgent}
                      />
                    ),
                  )}
            </div>
          )}
        </div>
      </div>

      {creating && (
        <CreateAgentModal
          onClose={() => setCreating(false)}
        />
      )}
      {testAgent && <TestCallModal agent={testAgent} onClose={() => setTestAgent(null)} />}
      </div>
    </div>
  );
}
